import { queryOptions } from "@tanstack/react-query";

import type { Money } from "@workspace/accounting/types";
import { apiUrl } from "@workspace/base-api/apiUrl";
import { fetchJson } from "@workspace/base-api/fetchJson";
import type { Operation } from "@workspace/operations";
import type { VenueDetails } from "@workspace/venue-management";
import type { Shift, StaffMember } from "@workspace/venue-management-staff";

import {
	writePath,
	type EightySixState,
	type EightySixStatus,
} from "./epos/eighty-six";
import type { ResendTicketsResponse } from "./epos/resend-warnings";
import type {
	CommitCourseFireResponse,
	SendToKitchenResponse,
} from "./epos/send-warnings";
import type {
	ApplyOrderDiscountInput,
	ClosedOrderSearchResult,
	ClosedTabSummary,
	CloseReason,
	CompInput,
	EposData,
	OpenCashDrawerWithoutSaleInput,
	Order,
	OrderDetailResponse,
	OrderDiscountResponse,
	OrderFigures,
	OrderRefund,
	PrintTabResult,
	RemoveOrderDiscountInput,
	SplitDefinitionInput,
	TabDetail,
	TabInput,
	TabMembershipResponse,
	TabPaymentRefundResult,
	TabPaymentResponse,
	TabUpdateInput,
	UnsentItemsResponse,
} from "./types";

/**
 * HA-768 / D-HA-24 24-2 — a line write (create, add, edit, remove) is cut off after this long, so
 * with its one retry a failure surfaces in about ten seconds: 2 × 4s, plus the pause between.
 */
export const WRITE_TIMEOUT_MS = 4_000;

export const kioskApi = {
	async get(tenant: string, venueId: string) {
		const url = new URL(`/venue-management/${venueId}/kiosk`, apiUrl);

		return await fetchJson<{
			staffMembers: StaffMember[];
			shifts: Shift[];
			venueDetails: VenueDetails;
		}>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	getOptions: (tenant: string, venueId: string) =>
		queryOptions({
			queryKey: [tenant, venueId, "kiosk"],
			queryFn: () => kioskApi.get(tenant, venueId),
		}),

	async getUserOperations(tenant: string, venueId: string, userId: string) {
		const url = new URL(
			`/venue-management/${venueId}/kiosk/users/${userId}/operations`,
			apiUrl,
		);

		return await fetchJson<Operation[]>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	getUserOperationsOptions: (
		tenant: string,
		venueId: string,
		userId: string | null | undefined,
	) =>
		queryOptions({
			queryKey: [tenant, venueId, "kiosk", "user-operations", userId],
			queryFn: () => kioskApi.getUserOperations(tenant, venueId, userId!),
			enabled: !!userId,
		}),
};

/** HA-658 — a comp route's POST: the server answers with the updated Order (its comps and figures), or a refusal as a problem body. */
async function postComp(tenant: string, path: string, input?: CompInput) {
	const response = await fetchJson<Order>(new URL(path, apiUrl).toString(), {
		headers: { tenant: tenant || "" },
		method: "POST",
		...(input ? { body: JSON.stringify(input) } : {}),
	});

	return { success: true as const, response };
}

export const eposApi = {
	async getEpos(tenant: string, venueId: string) {
		const url = new URL(`/venue-management/${venueId}/epos`, apiUrl);

		return await fetchJson<EposData>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	getEposOptions: (tenant: string, venueId: string) =>
		queryOptions({
			queryKey: [tenant, venueId, "epos"],
			queryFn: () => eposApi.getEpos(tenant, venueId),
		}),

	async sendOrder<T>(tenant: string, venueId: string, data: T) {
		const url = new URL(`/venue-management/${venueId}/epos/orders`, apiUrl);

		const response = await fetchJson<{ url: string }>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(data),
			timeoutMs: WRITE_TIMEOUT_MS,
		});

		return { success: true, response };
	},

	postOrderOptions: <T>(tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "send-order"],
		mutationFn: (data: T) => eposApi.sendOrder(tenant, venueId, data),
	}),
	async addItemToOrder(
		tenant: string,
		venueId: string,
		orderId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/items`,
			apiUrl,
		);

		const response = await fetchJson<Order>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(data),
			timeoutMs: WRITE_TIMEOUT_MS,
		});

		return { success: true, response };
	},

	addItemToOrderOptions: <T>(tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "add-item-to-order"],
		mutationFn: (data: T & { orderId: string }) =>
			eposApi.addItemToOrder(tenant, venueId, data.orderId, data),
	}),
	async moveItemToCourse(
		tenant: string,
		venueId: string,
		orderId: string,
		itemId: string,
		data: { toCourseId: string | null },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/items/${itemId}/course`,
			apiUrl,
		);

		const response = await fetchJson<Order>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "PATCH",
			body: JSON.stringify(data),
		});

		return { success: true, response };
	},
	moveItemToCourseOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "move-item-to-course"],
		mutationFn: (data: {
			orderId: string;
			itemId: string;
			toCourseId: string | null;
		}) =>
			eposApi.moveItemToCourse(tenant, venueId, data.orderId, data.itemId, {
				toCourseId: data.toCourseId,
			}),
	}),
	// HA-554 Part 2 — complete-order takes no body: the server settles on its own recorded payments.
	async completeOrder(tenant: string, venueId: string, orderId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/complete-order`,
			apiUrl,
		);

		// HA-777 — 204 by contract (CompleteOrderEndpoint returns void): there is no body to read.
		// This used to hand fetchJson's `{}` on as an Order, and HA-767 dispatched it.
		await fetchJson<void>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
		});

		return { success: true as const };
	},

	completeOrderOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "add-item-to-order"],
		mutationFn: (data: { orderId: string }) =>
			eposApi.completeOrder(tenant, venueId, data.orderId),
	}),

	async closeOrder(
		tenant: string,
		venueId: string,
		orderId: string,
		input: { reason: CloseReason; notes?: string | null },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/close`,
			apiUrl,
		);

		const response = await fetchJson<EposData>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});

		return { success: true, response };
	},

	closeOrderOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "close-order"],
		mutationFn: (data: {
			orderId: string;
			reason: CloseReason;
			notes?: string | null;
		}) =>
			eposApi.closeOrder(tenant, venueId, data.orderId, {
				reason: data.reason,
				notes: data.notes,
			}),
	}),

	async dismissOrder(tenant: string, venueId: string, orderId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/dismiss`,
			apiUrl,
		);

		const response = await fetchJson<EposData>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({}),
		});

		return { success: true, response };
	},

	/** HA-357 slice C (AC9, AC18) — takes the service charge off an open order, or puts it back; answers with the order's figures. */
	async setServiceChargeRemoved(
		tenant: string,
		venueId: string,
		orderId: string,
		removed: boolean,
	) {
		const action = removed ? "remove" : "add";
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/service-charge/${action}`,
			apiUrl,
		);

		return fetchJson<OrderFigures>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({}),
		});
	},

	serviceChargeOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "service-charge"],
		mutationFn: (data: { orderId: string; removed: boolean }) =>
			eposApi.setServiceChargeRemoved(
				tenant,
				venueId,
				data.orderId,
				data.removed,
			),
	}),

	/**
	 * HA-885 / HA-15: applies a discount to the order or one line, or modifies the one already there (one per target). The server
	 * answers with the order's figures and discounts; nothing is shown as applied until then. Refusals come back as the server's
	 * problem (400 names its rule in `extensions.code`; 409 is the money-change guard).
	 */
	async applyOrderDiscount(
		tenant: string,
		venueId: string,
		orderId: string,
		input: ApplyOrderDiscountInput,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/discounts`,
			apiUrl,
		);

		return fetchJson<OrderDiscountResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});
	},

	/** HA-885 / HA-15: removes the discount on a target. It takes no reason; the server's event carries the removed discount's own. */
	async removeOrderDiscount(
		tenant: string,
		venueId: string,
		orderId: string,
		input: RemoveOrderDiscountInput,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/discounts/remove`,
			apiUrl,
		);

		return fetchJson<OrderDiscountResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});
	},

	applyOrderDiscountOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "order-discount", "apply"],
		mutationFn: (data: { orderId: string } & ApplyOrderDiscountInput) => {
			const { orderId, ...input } = data;
			return eposApi.applyOrderDiscount(tenant, venueId, orderId, input);
		},
	}),

	removeOrderDiscountOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "order-discount", "remove"],
		mutationFn: (data: { orderId: string } & RemoveOrderDiscountInput) => {
			const { orderId, ...input } = data;
			return eposApi.removeOrderDiscount(tenant, venueId, orderId, input);
		},
	}),

	dismissOrderOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "dismiss-order"],
		mutationFn: (data: { orderId: string }) =>
			eposApi.dismissOrder(tenant, venueId, data.orderId),
	}),

	/** HA-619 — the send now reports what it couldn't reach; see `epos/send-warnings.ts`. */
	async sendToKitchen(
		tenant: string,
		venueId: string,
		orderId: string,
		input: { itemIds?: string[]; deviceId?: string; sendId?: string },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/send-to-kitchen`,
			apiUrl,
		);

		const response = await fetchJson<SendToKitchenResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});

		return { success: true, response };
	},

	sendToKitchenOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "send-to-kitchen"],
		mutationFn: (data: {
			orderId: string;
			itemIds?: string[];
			deviceId?: string;
			sendId?: string;
		}) =>
			eposApi.sendToKitchen(tenant, venueId, data.orderId, {
				itemIds: data.itemIds,
				deviceId: data.deviceId,
				// HA-588 — keyed on this, so a retry after a reconnect deducts once.
				sendId: data.sendId,
			}),
	}),

	async initiateCourseFire(
		tenant: string,
		venueId: string,
		orderId: string,
		input: { deviceId?: string },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/fire-course`,
			apiUrl,
		);

		const response = await fetchJson<Order>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});

		return { success: true, response };
	},

	initiateCourseFireOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "initiate-course-fire"],
		mutationFn: (data: { orderId: string; deviceId?: string }) =>
			eposApi.initiateCourseFire(tenant, venueId, data.orderId, {
				deviceId: data.deviceId,
			}),
	}),

	async commitCourseFire(
		tenant: string,
		venueId: string,
		orderId: string,
		input: { deviceId?: string } = {},
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/fire-course/commit`,
			apiUrl,
		);

		const response = await fetchJson<CommitCourseFireResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});

		return { success: true, response };
	},

	commitCourseFireOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "commit-course-fire"],
		// HA-619 — the device decides which queues are suppressed for this terminal, so a warning
		// can only exclude them (AC6) if the send says which device it came from.
		mutationFn: (data: { orderId: string; deviceId?: string }) =>
			eposApi.commitCourseFire(tenant, venueId, data.orderId, {
				deviceId: data.deviceId,
			}),
	}),

	async cancelCourseFire(tenant: string, venueId: string, orderId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/fire-course/cancel`,
			apiUrl,
		);

		const response = await fetchJson<Order>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({}),
		});

		return { success: true, response };
	},

	cancelCourseFireOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "cancel-course-fire"],
		mutationFn: (data: { orderId: string }) =>
			eposApi.cancelCourseFire(tenant, venueId, data.orderId),
	}),

	/**
	 * HA-570: the endpoint reports which queues it dispatched to AND which were skipped because no
	 * printer is mapped to them. Discarding the body is what left the zero-printer case silent —
	 * the operator saw "resent" and nothing printed.
	 */
	async resendTickets(
		tenant: string,
		venueId: string,
		orderId: string,
		input: { courseId: string },
	): Promise<ResendTicketsResponse> {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/resend-tickets`,
			apiUrl,
		);

		return await fetchJson<ResendTicketsResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});
	},

	resendTicketsOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "resend-tickets"],
		mutationFn: (data: { orderId: string; courseId: string }) =>
			eposApi.resendTickets(tenant, venueId, data.orderId, {
				courseId: data.courseId,
			}),
	}),

	// HA-583 — print a customer bill/receipt for an order to the terminal's assigned receipt printer.
	async printReceipt(
		tenant: string,
		venueId: string,
		orderId: string,
		input: { deviceId: string },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/print-receipt`,
			apiUrl,
		);

		return await fetchJson<{
			documentType: "Bill" | "Receipt";
			duplicate: boolean;
			counter: number;
			absorbed?: boolean;
		}>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});
	},

	printReceiptOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "print-receipt"],
		mutationFn: (data: { orderId: string; deviceId: string }) =>
			eposApi.printReceipt(tenant, venueId, data.orderId, {
				deviceId: data.deviceId,
			}),
	}),

	/**
	 * HA-658 / D-HA-24 — Comp and Remove Comp are server-confirmed: nothing shows as comped until the server answers with the order
	 * (its comps and figures). The four routes are one class each on the server; a refusal comes back as its problem body.
	 */
	async compItem(
		tenant: string,
		venueId: string,
		orderId: string,
		itemId: string,
		input: CompInput,
	) {
		return await postComp(
			tenant,
			`/venue-management/${venueId}/epos/orders/${orderId}/items/${itemId}/comp`,
			input,
		);
	},

	async removeItemComp(
		tenant: string,
		venueId: string,
		orderId: string,
		itemId: string,
	) {
		return await postComp(
			tenant,
			`/venue-management/${venueId}/epos/orders/${orderId}/items/${itemId}/comp/remove`,
		);
	},

	async compOrder(
		tenant: string,
		venueId: string,
		orderId: string,
		input: CompInput,
	) {
		return await postComp(
			tenant,
			`/venue-management/${venueId}/epos/orders/${orderId}/comp`,
			input,
		);
	},

	async removeOrderComp(tenant: string, venueId: string, orderId: string) {
		return await postComp(
			tenant,
			`/venue-management/${venueId}/epos/orders/${orderId}/comp/remove`,
		);
	},

	compItemOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "comp", "item"],
		mutationFn: (data: { orderId: string; itemId: string; reason: string }) =>
			eposApi.compItem(tenant, venueId, data.orderId, data.itemId, {
				reason: data.reason,
			}),
	}),

	removeItemCompOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "comp", "remove-item"],
		mutationFn: (data: { orderId: string; itemId: string }) =>
			eposApi.removeItemComp(tenant, venueId, data.orderId, data.itemId),
	}),

	compOrderOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "comp", "order"],
		mutationFn: (data: { orderId: string; reason: string }) =>
			eposApi.compOrder(tenant, venueId, data.orderId, { reason: data.reason }),
	}),

	removeOrderCompOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "comp", "remove-order"],
		mutationFn: (data: { orderId: string }) =>
			eposApi.removeOrderComp(tenant, venueId, data.orderId),
	}),

	async saveOrder(
		tenant: string,
		venueId: string,
		orderId: string,
		input: { deviceId?: string },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/save`,
			apiUrl,
		);

		const response = await fetchJson<Order>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});

		return { success: true, response };
	},

	saveOrderOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "save-order"],
		mutationFn: (data: { orderId: string; deviceId?: string }) =>
			eposApi.saveOrder(tenant, venueId, data.orderId, {
				deviceId: data.deviceId,
			}),
	}),

	async getUnsentItems(tenant: string, venueId: string, orderId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/unsent-items`,
			apiUrl,
		);

		return await fetchJson<UnsentItemsResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "GET",
		});
	},

	getUnsentItemsOptions: (
		tenant: string,
		venueId: string,
		orderId: string | null,
	) =>
		queryOptions({
			queryKey: [tenant, venueId, "epos", "unsent-items", orderId],
			queryFn: () => eposApi.getUnsentItems(tenant, venueId, orderId!),
			enabled: !!orderId,
		}),

	async removeUnsentItems(tenant: string, venueId: string, orderId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/remove-unsent-items`,
			apiUrl,
		);

		await fetchJson(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({}),
		});

		return { success: true };
	},

	removeUnsentItemsOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "remove-unsent-items"],
		mutationFn: (data: { orderId: string }) =>
			eposApi.removeUnsentItems(tenant, venueId, data.orderId),
	}),

	/**
	 * HA-588 — `returnedToStock` is the operator's answer to "Returned to stock?" on a line that
	 * was already sent. It only reaches the server as the truth someone asserted; a line that was
	 * never sent deducted nothing, so the server ignores it there.
	 */
	async removeItemToOrder(
		tenant: string,
		venueId: string,
		orderId: string,
		itemId: string,
		data: Record<string, unknown> & {
			returnedToStock?: boolean;
			removalId?: string;
		},
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/items/${itemId}`,
			apiUrl,
		);

		if (data.returnedToStock === true) {
			url.searchParams.set("returnedToStock", "true");
		}

		// HA-265: minted once per removal on the till and kept on a retry, so a replay is one removal.
		if (data.removalId) url.searchParams.set("removalId", data.removalId);

		const response = await fetchJson<Order>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "DELETE",
			body: JSON.stringify(data),
			timeoutMs: WRITE_TIMEOUT_MS,
		});

		return { success: true, response };
	},

	removeItemToOrderOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "add-item-to-order"],
		mutationFn: (
			data: Record<string, unknown> & {
				orderId: string;
				id: string;
				returnedToStock?: boolean;
				removalId?: string;
			},
		) =>
			eposApi.removeItemToOrder(tenant, venueId, data.orderId, data.id, data),
	}),

	/** HA-265: puts the most recent removal back (one level). The server answers with the order, the line at its old position. */
	async restoreItem(
		tenant: string,
		venueId: string,
		orderId: string,
		removalId: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/items/restore`,
			apiUrl,
		);

		const response = await fetchJson<Order>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({ removalId }),
			timeoutMs: WRITE_TIMEOUT_MS,
		});

		return { success: true, response };
	},

	restoreItemOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "restore-item"],
		mutationFn: (data: { orderId: string; removalId: string }) =>
			eposApi.restoreItem(tenant, venueId, data.orderId, data.removalId),
	}),

	async createTab(tenant: string, venueId: string, tab: TabInput) {
		const url = new URL(`/venue-management/${venueId}/epos/tabs`, apiUrl);

		await fetchJson<void>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(tab),
		});

		return { success: true };
	},

	createTabOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "create-tab"],
		mutationFn: (tab: TabInput) => eposApi.createTab(tenant, venueId, tab),
	}),

	async updateTab(
		tenant: string,
		venueId: string,
		tabId: string,
		tab: TabUpdateInput,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/tabs/${tabId}`,
			apiUrl,
		);

		const response = await fetchJson<EposData>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "PUT",
			body: JSON.stringify(tab),
		});

		return { success: true, response };
	},

	updateTabOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "update-tab"],
		mutationFn: (data: { tabId: string; tab: TabUpdateInput }) =>
			eposApi.updateTab(tenant, venueId, data.tabId, data.tab),
	}),

	async openCashDrawerWithoutSale(
		tenant: string,
		venueId: string,
		input: OpenCashDrawerWithoutSaleInput,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/cash-drawer/open-without-sale`,
			apiUrl,
		);

		const response = await fetchJson<EposData>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});

		return { success: true, response };
	},

	openCashDrawerWithoutSaleOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "open-cash-drawer-without-sale"],
		mutationFn: (input: OpenCashDrawerWithoutSaleInput) =>
			eposApi.openCashDrawerWithoutSale(tenant, venueId, input),
	}),

	/** HA-554 — a tab's detail: its orders (oldest first) and the server's figures. A closed tab still reads. */
	getTabOptions: (tenant: string, venueId: string, tabId: string) =>
		queryOptions({
			queryKey: [tenant, venueId, "epos", "tabs", tabId],
			queryFn: () =>
				fetchJson<TabDetail>(
					new URL(
						`/venue-management/${venueId}/epos/tabs/${tabId}`,
						apiUrl,
					).toString(),
					{ headers: { tenant: tenant || "" } },
				),
		}),

	/**
	 * HA-360 Part B — print the tab's bill (Open) or receipt (Closed and settled) to this terminal's receipt printer. The
	 * server decides which; a second print of the same document is a duplicate.
	 */
	async printTab(
		tenant: string,
		venueId: string,
		tabId: string,
		input: { deviceId: string },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/tabs/${tabId}/print`,
			apiUrl,
		);

		return await fetchJson<PrintTabResult>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});
	},

	printTabOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "print-tab"],
		mutationFn: (data: { tabId: string; deviceId: string }) =>
			eposApi.printTab(tenant, venueId, data.tabId, {
				deviceId: data.deviceId,
			}),
	}),

	/** HA-360 Part B — the tabs closed today (the venue's day), newest first, to reprint a Tab receipt from. */
	getClosedTabsOptions: (tenant: string, venueId: string) =>
		queryOptions({
			queryKey: [tenant, venueId, "epos", "tabs", { status: "Closed" }],
			queryFn: () => {
				const url = new URL(`/venue-management/${venueId}/epos/tabs`, apiUrl);
				url.searchParams.set("status", "Closed");
				return fetchJson<ClosedTabSummary[]>(url.toString(), {
					headers: { tenant: tenant || "" },
				});
			},
		}),

	/**
	 * HA-357 slice D — Remove or Add the tab's service charge line (epos:tabs:manage). Idempotent; answers with the tab.
	 * Refused once any of the line is paid, and on a tab with no line.
	 */
	async setTabServiceCharge(
		tenant: string,
		venueId: string,
		tabId: string,
		action: "remove" | "add",
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/tabs/${tabId}/service-charge/${action}`,
			apiUrl,
		);

		return await fetchJson<TabDetail>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({}),
		});
	},

	setTabServiceChargeOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "tab-service-charge"],
		mutationFn: (data: { tabId: string; action: "remove" | "add" }) =>
			eposApi.setTabServiceCharge(tenant, venueId, data.tabId, data.action),
	}),

	/**
	 * HA-789 — refund a Pay Tab payment (epos:refund:create): all that is left (amount null) or an amount, spread over what it
	 * paid; the tip too only on a full refund. Answers with the refund and the tab.
	 */
	async refundTabPayment(
		tenant: string,
		venueId: string,
		tabId: string,
		tabPaymentId: string,
		data: {
			amount: Money | null;
			refundTip: boolean;
			reason: string;
			notes?: string | null;
		},
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/tabs/${tabId}/payments/${tabPaymentId}/refunds`,
			apiUrl,
		);

		return await fetchJson<TabPaymentRefundResult>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(data),
		});
	},

	refundTabPaymentOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "tab-payment-refund"],
		mutationFn: (data: {
			tabId: string;
			tabPaymentId: string;
			amount: Money | null;
			refundTip: boolean;
			reason: string;
			notes?: string | null;
		}) => {
			const { tabId, tabPaymentId, ...body } = data;
			return eposApi.refundTabPayment(
				tenant,
				venueId,
				tabId,
				tabPaymentId,
				body,
			);
		},
	}),

	/**
	 * HA-983 — retry a Failed Pay Tab card refund (epos:refund:create): the same refund and key, so Stripe cannot refund it twice. Answers with
	 * the refund as it stands after the attempt, and the tab.
	 */
	async retryTabRefund(
		tenant: string,
		venueId: string,
		tabId: string,
		refundId: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/tabs/${tabId}/refunds/${refundId}/retry`,
			apiUrl,
		);

		return await fetchJson<TabPaymentRefundResult>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
		});
	},

	retryTabRefundOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "tab-refund-retry"],
		mutationFn: (data: { tabId: string; refundId: string }) =>
			eposApi.retryTabRefund(tenant, venueId, data.tabId, data.refundId),
	}),

	/** HA-554 — Close tab by hand (epos:tabs:manage); refused while anything is outstanding. */
	async closeTab(tenant: string, venueId: string, tabId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/tabs/${tabId}/close`,
			apiUrl,
		);

		return await fetchJson<TabDetail>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({}),
		});
	},

	/**
	 * HA-554 Part 2 — Pay Tab cash: `amount` is what is paid on the tab, `tendered` the cash handed over. Answers with the
	 * server's allocation and the tab after it (closed if this settled it).
	 */
	async payTabCash(
		tenant: string,
		venueId: string,
		tabId: string,
		data: { paymentId: string; amount: Money; tendered: Money; tip?: Money },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/tabs/${tabId}/cash-payments`,
			apiUrl,
		);

		return await fetchJson<TabPaymentResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(data),
		});
	},

	payTabCashOptions: (tenant: string, venueId: string, tabId: string) => ({
		mutationKey: [tenant, venueId, "epos", "tabs", tabId, "pay-cash"],
		mutationFn: (data: {
			paymentId: string;
			amount: Money;
			tendered: Money;
			tip?: Money;
		}) => eposApi.payTabCash(tenant, venueId, tabId, data),
	}),

	closeTabOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "close-tab"],
		mutationFn: (data: { tabId: string }) =>
			eposApi.closeTab(tenant, venueId, data.tabId),
	}),

	async assignOrderToTab(
		tenant: string,
		venueId: string,
		orderId: string,
		tabId: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/assign-to-tab`,
			apiUrl,
		);

		const response = await fetchJson<TabMembershipResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "PUT",
			body: JSON.stringify({ tabId }),
		});

		return { success: true, response };
	},

	assignOrderToTabOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "assign-order-to-tab"],
		mutationFn: (data: { orderId: string; tabId: string }) =>
			eposApi.assignOrderToTab(tenant, venueId, data.orderId, data.tabId),
	}),

	async moveOrderToTab(
		tenant: string,
		venueId: string,
		orderId: string,
		toTabId: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/move-to-tab`,
			apiUrl,
		);

		const response = await fetchJson<TabMembershipResponse>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "PUT",
			body: JSON.stringify({ toTabId }),
		});

		return { success: true, response };
	},

	moveOrderToTabOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "move-order-to-tab"],
		mutationFn: (data: { orderId: string; toTabId: string }) =>
			eposApi.moveOrderToTab(tenant, venueId, data.orderId, data.toTabId),
	}),

	async createRefund(
		tenant: string,
		venueId: string,
		orderId: string,
		input: {
			type: string;
			method: string;
			reason: string;
			notes?: string | null;
			paymentId?: string | null;
			customAmount?: {
				value: number;
				currency: string;
				version: 2;
			} | null;
			items: Array<{ itemId: string; quantity: number }>;
			/** HA-858 (S4) — hand back the tip too (honoured on a full refund of one tipped payment). */
			refundTip?: boolean;
		},
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/refunds`,
			apiUrl,
		);

		// HA-858 (S4, planning's addendum): the endpoint answers the order and the refund it made.
		const response = await fetchJson<{ order: Order; refund?: OrderRefund }>(
			url.toString(),
			{
				headers: { tenant: tenant || "" },
				method: "POST",
				body: JSON.stringify(input),
			},
		);

		return { success: true, order: response.order, refund: response.refund };
	},

	createRefundOptions: (tenant: string, venueId: string, orderId: string) => ({
		mutationKey: [tenant, venueId, "epos", "orders", orderId, "refunds"],
		mutationFn: (input: Parameters<typeof eposApi.createRefund>[3]) =>
			eposApi.createRefund(tenant, venueId, orderId, input),
	}),

	// HA-607 — split-bill backend persistence. The backend owns the split
	// definition + per-split status; the frontend reads `order.activeSplitGroup`
	// for live state and uses these mutations to mutate it.
	async initiateSplit(
		tenant: string,
		venueId: string,
		orderId: string,
		definition: SplitDefinitionInput,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/splits`,
			apiUrl,
		);
		return await fetchJson<{ id: string }>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({ definition }),
		});
	},

	initiateSplitOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "splits", "initiate"],
		mutationFn: (input: {
			orderId: string;
			definition: SplitDefinitionInput;
		}) =>
			eposApi.initiateSplit(tenant, venueId, input.orderId, input.definition),
	}),

	async cancelSplit(tenant: string, venueId: string, orderId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/splits/cancel`,
			apiUrl,
		);
		return await fetchJson<unknown>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify({}),
		});
	},

	cancelSplitOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "splits", "cancel"],
		mutationFn: (input: { orderId: string }) =>
			eposApi.cancelSplit(tenant, venueId, input.orderId),
	}),

	async recordCashPayment(
		tenant: string,
		venueId: string,
		orderId: string,
		input: {
			paymentId: string;
			amount: { value: number; currency: string };
			tip?: { value: number; currency: string } | null;
			splitId?: string | null;
		},
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/cash-payments`,
			apiUrl,
		);
		return await fetchJson<{ paymentId: string }>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(input),
		});
	},

	recordCashPaymentOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "cash-payments"],
		mutationFn: (input: {
			orderId: string;
			payload: Parameters<typeof eposApi.recordCashPayment>[3];
		}) =>
			eposApi.recordCashPayment(tenant, venueId, input.orderId, input.payload),
	}),

	async retryRefund(
		tenant: string,
		venueId: string,
		orderId: string,
		refundId: string,
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/refunds/${refundId}/retry`,
			apiUrl,
		);

		const response = await fetchJson<{
			success: boolean;
			message?: string;
			refund: Order["refunds"] extends Array<infer R> ? R : never;
		}>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
		});

		return response;
	},

	retryRefundOptions: (
		tenant: string,
		venueId: string,
		orderId: string,
		refundId: string,
	) => ({
		mutationKey: [
			tenant,
			venueId,
			"epos",
			"orders",
			orderId,
			"refunds",
			refundId,
			"retry",
		],
		mutationFn: () => eposApi.retryRefund(tenant, venueId, orderId, refundId),
	}),

	async searchClosedOrders(
		tenant: string,
		venueId: string,
		params: { page?: number; pageSize?: number; query?: string },
	) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/search`,
			apiUrl,
		);

		return await fetchJson<ClosedOrderSearchResult>(url.toString(), {
			headers: { tenant: tenant || "" },
			method: "POST",
			body: JSON.stringify(params),
		});
	},

	searchClosedOrdersOptions: (
		tenant: string,
		venueId: string,
		params: { pageSize?: number; query?: string },
	) => ({
		queryKey: [tenant, venueId, "epos", "closed-orders", params],
		queryFn: ({ pageParam }: { pageParam: number }) =>
			eposApi.searchClosedOrders(tenant, venueId, {
				...params,
				page: pageParam,
			}),
	}),

	async getOrderDetail(tenant: string, venueId: string, orderId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}`,
			apiUrl,
		);

		return await fetchJson<OrderDetailResponse>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	getOrderDetailOptions: (
		tenant: string,
		venueId: string,
		orderId: string | null,
	) =>
		queryOptions({
			queryKey: [tenant, venueId, "epos", "orders", orderId],
			queryFn: () => eposApi.getOrderDetail(tenant, venueId, orderId!),
			enabled: !!orderId,
		}),
};

/**
 * HA-591 — the 86 surface the till talks to.
 *
 * Status is a POST because it takes the subjects being drawn: the answer is derived per dish, and
 * deriving it for a whole catalogue to render one menu screen is work nobody reads. Clearing is a
 * POST too, not a DELETE — `StockItemNoHardDeleteTests` asserts that no Stock route exposes a
 * DELETE verb at all, and a verb-level guarantee stops being one the moment it has an exception.
 */
export const eightySixApi = {
	async getStatus(
		tenant: string,
		venueId: string,
		subjects: { productId: string; variantId: string | null }[],
	) {
		const url = new URL(
			`/venue-management/stock/venues/${venueId}/86/status`,
			apiUrl,
		);

		return await fetchJson<{ items: EightySixStatus[] }>(url.toString(), {
			method: "POST",
			headers: { tenant: tenant || "" },
			body: JSON.stringify(subjects),
		});
	},

	/**
	 * Polled rather than pushed. The terminal has no hub client — `epos-context/helpers.ts` still
	 * carries the `EposHub.invoke` calls commented out — and ten seconds is the freshness idiom
	 * already used by the epos display and the kiosk overview, which is the AC's ceiling.
	 */
	getStatusOptions: (
		tenant: string,
		venueId: string,
		subjects: { productId: string; variantId: string | null }[],
	) =>
		queryOptions({
			queryKey: [tenant, venueId, "86", "status", subjects],
			queryFn: () => eightySixApi.getStatus(tenant, venueId, subjects),
			refetchInterval: 10_000,
			enabled: subjects.length > 0,
		}),

	async write(
		tenant: string,
		venueId: string,
		action: "set" | "clear" | "override",
		input: { productId: string; variantId: string | null; note?: string },
	) {
		const url = new URL(
			`/venue-management/stock/venues/${venueId}/${writePath(action)}`,
			apiUrl,
		);

		return await fetchJson<EightySixState>(url.toString(), {
			method: "POST",
			headers: { tenant: tenant || "" },
			body: JSON.stringify(input),
		});
	},
};
